// src/pages/projects/ProjectDetail.jsx

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where } from "firebase/firestore";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { fetchChecklistItems, calculateProjectProgress } from "./projectService";
import { canAccessProject } from "./projectAccess";
import QuickCreateSite   from "../../components/quickcreate/forms/QuickCreateSite";
import QuickCreateModal  from "../../components/quickcreate/QuickCreateModal";
import CostingCard       from "../../components/CostingCard";
// Project Hub tabs — the existing pages, rendered inside this page with
// embedded={true} (hides their own back-link). Their logic is unchanged and
// their standalone routes still work exactly as before.
import ProjectChecklist  from "./ProjectChecklist";
import ProjectDocuments  from "./ProjectDocuments";
import ProjectMaterials  from "./ProjectMaterials";


const STATUS_OPTIONS = ["active", "on_hold", "completed", "cancelled"];

const STATUS_COLORS = {
  active:    "bg-green-100 text-green-700",
  on_hold:   "bg-yellow-100 text-yellow-700",
  completed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-700",
};

// eslint-disable-next-line no-unused-vars
const CATEGORY_COLORS = {
  approval:      "bg-purple-100 text-purple-700",
  execution:     "bg-blue-100 text-blue-700",
  payment:       "bg-green-100 text-green-700",
  documentation: "bg-orange-100 text-orange-700",
};

export default function ProjectDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const role         = user.role || "";
  const isTechnician = role === "technician";
  const isSiteEngineer = role === "site_engineer";
  // Only office roles see money (contract value, costing, milestones) and
  // can edit the project or change its status. Field roles (site engineer,
  // technician) see execution info only.
  const isManager    = ["sm", "owner", "manager"].includes(role);
  const [project,          setProject]          = useState(null);
  const [checklist,        setChecklist]         = useState([]);
  const [linkedServices,   setLinkedServices]    = useState([]);
  const [loading,          setLoading]           = useState(true);
  const [saving,           setSaving]            = useState(false);
  const [editOpen,  setEditOpen]  = useState(false);
  const [editForm,  setEditForm]  = useState({});
  const [clients,       setClients]       = useState([]);
  const [sites,         setSites]         = useState([]);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [siteEngineers, setSiteEngineers] = useState([]);

  // ── PROJECT HUB TABS ────────────────────────────────────────────────
  // Tab list mirrors App.js route access exactly: technician only has the
  // checklist route; site engineer and office roles also have materials
  // and documents. A tab a role can't use is never shown, and an unknown
  // ?tab= value falls back to Overview.
  const TABS = [
    { key: "overview",  label: "📊 Overview" },
    { key: "checklist", label: "📋 Checklist" },
    ...(isTechnician ? [] : [
      { key: "materials", label: "📦 Material" },
      { key: "documents", label: "📁 Documents" },
    ]),
  ];
  // Active tab lives in the URL (?tab=...) so refresh, browser Back and
  // shared links all land on the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") || "overview";
  const activeTab = TABS.some(t => t.key === requestedTab) ? requestedTab : "overview";
  const setTab = (key) => {
    setSearchParams(key === "overview" ? {} : { tab: key });
    // Ticking items in the Checklist tab changes progress — refresh the
    // Overview numbers whenever the user comes back to it.
    if (key === "overview") fetchChecklist();
  };

  useEffect(() => { if (id) { fetchProject(); fetchChecklist(); fetchLinkedServices(); } }, [id]); // eslint-disable-line

  const fetchProject = async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, "projects", id));
      if (!snap.exists()) { alert("Project not found"); navigate("/projects"); return; }
      const data = snap.data();
      // Company + assignment check — one shared rule (projectAccess.js),
      // the same one Checklist / Documents / Materials use.
      if (!(await canAccessProject(data, snap.id, user))) {
        alert("Access denied — you are not assigned to this project");
        navigate("/projects");
        return;
      }

      setProject({ id: snap.id, ...data });
    } catch (err) {
      console.error("fetchProject error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    const snap = await getDocs(query(
      collection(db, "clients"), where("company_id", "==", company_id)
    ));
    setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchSites = async (clientId) => {
    if (!clientId) return;
    const snap = await getDocs(query(
      collection(db, "sites"),
      where("company_id", "==", company_id),
      where("client_id",  "==", clientId)
    ));
    setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  // Site Engineers — company-wide list, for the assignment picker in the
  // Edit modal. PM assigns which projects a Site Engineer can see; this
  // powers projects.site_engineer_ids used by the scoped route-guard in
  // App.js (role === "site_engineer" reads this same field).
  const fetchSiteEngineers = async () => {
    const snap = await getDocs(query(
      collection(db, "users"),
      where("company_id", "==", company_id),
      where("role", "==", "site_engineer")
    ));
    setSiteEngineers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const openEdit = () => {
    setEditForm({
      project_name: project.project_name || "",
      client_id:    project.client_id    || "",
      client_name:  project.client_name  || "",
      site_id:      project.site_id      || "",
      site_name:    project.site_name    || "",
      project_type: project.project_type || "installation",
      total_value:  project.total_value  || "",
      start_date:   project.start_date   || "",
      end_date:     project.end_date     || "",
      description:  project.description  || "",
      contacts:     project.contacts     || [{ name: "", role: "", phone: "" }],
      site_engineer_ids: project.site_engineer_ids || [],
    });
    fetchClients();
    fetchSiteEngineers();
    if (project.client_id) fetchSites(project.client_id);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.project_name.trim()) { alert("Project name required"); return; }
    try {
      setSaving(true);
      const validContacts = (editForm.contacts || []).filter(c => c.name?.trim());
      await updateDoc(doc(db, "projects", id), {
        project_name: editForm.project_name,
        client_id:    editForm.client_id    || "",
        client_name:  editForm.client_name  || "",
        site_id:      editForm.site_id      || "",
        site_name:    editForm.site_name    || "",
        project_type: editForm.project_type || "installation",
        total_value:  Number(editForm.total_value || 0),
        start_date:   editForm.start_date   || "",
        end_date:     editForm.end_date     || "",
        description:  editForm.description  || "",
        contacts:     validContacts,
        site_engineer_ids: editForm.site_engineer_ids || [],
        updated_at:   serverTimestamp(),
      });
      setProject(p => ({ ...p, ...editForm, contacts: validContacts }));
      setEditOpen(false);
      alert("Project updated ✅");
    } catch (err) {
      alert("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const fetchChecklist = async () => {
    try {
      const items = await fetchChecklistItems(id, company_id);
      setChecklist(items);
    } catch (err) {
      console.error("fetchChecklist error:", err);
    }
  };

  const fetchLinkedServices = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "service_tasks"),
        where("company_id", "==", company_id),
        where("project_id",  "==", id)
      ));
      setLinkedServices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("fetchLinkedServices error:", err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Change status to "${newStatus}"?`)) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, "projects", id), {
        status:     newStatus,
        updated_at: serverTimestamp(),
      });
      setProject(p => ({ ...p, status: newStatus }));
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  // Checklist summary — centralized
  const { total: totalItems, completed: completedItems,
          pct: progressPct, milestones } = calculateProjectProgress(checklist);
  const pendingMilestones = milestones.filter(i => i.status !== "completed");

  if (loading) return <div className="p-6 text-gray-500">Loading project...</div>;
  if (!project) return <div className="p-6 text-gray-500">Project not found.</div>;

  const fmtDate = (ts) => {
    if (!ts) return "—";
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString("en-IN");
    return ts;
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto">

      {/* HEADER */}
      <div className="flex justify-between items-start gap-3">
        <div>
          <button onClick={() => navigate("/projects")}
            className="text-gray-400 text-sm hover:text-gray-600 mb-1 block">
            ← Projects
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{project.project_name}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{project.client_name} {project.site_name ? `— ${project.site_name}` : ""}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isManager && (
            <button onClick={openEdit}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-200">
              ✏️ Edit
            </button>
          )}
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
            STATUS_COLORS[project.status] || "bg-gray-100 text-gray-600"
          }`}>
            {project.status?.replace("_", " ").toUpperCase()}
          </span>
        </div>
      </div>

      {/* HUB TABS */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap transition ${
              activeTab === t.key
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "checklist" && <ProjectChecklist embedded />}
      {activeTab === "materials" && <ProjectMaterials embedded />}
      {activeTab === "documents" && <ProjectDocuments embedded />}

      {activeTab === "overview" && (<>
      {/* SUMMARY CARDS */}
      <div className={`grid grid-cols-2 gap-3 ${isManager ? "md:grid-cols-4" : "md:grid-cols-2"}`}>
        {isManager && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-700">
              ₹{Number(project.total_value || 0).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-gray-500 mt-1">Contract Value</div>
          </div>
        )}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">          <div className="text-2xl font-bold text-blue-700">{progressPct}%</div>
          <div className="text-xs text-gray-500 mt-1">
            {completedItems}/{totalItems} items done
          </div>
        </div>
        {isManager && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-purple-700">
              {pendingMilestones.length}
            </div>
            <div className="text-xs text-gray-500 mt-1">Pending Milestones</div>
          </div>
        )}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="text-sm font-semibold text-gray-700 capitalize">
            {project.project_type || "—"}
          </div>
          <div className="text-xs text-gray-500 mt-1">Project Type</div>
        </div>
      </div>

      {/* PROGRESS BAR */}
      {totalItems > 0 && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-semibold text-gray-700">Overall Progress</span>
            <span className="text-gray-500">{completedItems} of {totalItems} complete</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3">
            <div
              className="bg-green-500 h-3 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {/* PROJECT COSTING — Budget vs Actual, category breakdown, health
          indicator. Manager/Owner/SM only — same financial-visibility
          gate as the rest of this page's money-related sections. */}
      {isManager && (
        <CostingCard
          companyId={company_id}
          projectId={id}
          contractValue={project.total_value}
          progressPct={progressPct}
        />
      )}

      {/* PROJECT INFO */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-3">
        <h2 className="font-semibold text-gray-700">Project Details</h2>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <div>
            <span className="text-gray-400 block text-xs">Start Date</span>
            <span className="font-medium">{project.start_date || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">End Date</span>
            <span className="font-medium">{project.end_date || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Created</span>
            <span className="font-medium">{fmtDate(project.created_at)}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Created By</span>
            <span className="font-medium">{project.created_by_name || "—"}</span>
          </div>
          {project.description && (
            <div className="md:col-span-2">
              <span className="text-gray-400 block text-xs">Description</span>
              <span className="font-medium">{project.description}</span>
            </div>
          )}
        </div>
      </div>

      {/* CONTACTS */}
      {project.contacts?.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h2 className="font-semibold text-gray-700 mb-3">Site Contacts</h2>
          <div className="space-y-2">
            {project.contacts.map((c, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center font-semibold text-gray-600 shrink-0">
                  {c.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <span className="font-medium">{c.name}</span>
                  {c.role && <span className="text-gray-400 ml-2">({c.role})</span>}
                </div>
                {c.phone && (
                  <a href={`tel:${c.phone}`}
                    className="text-blue-600 hover:underline text-xs">
                    {c.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MILESTONE SUMMARY — financial, hidden from technician */}
      {isManager && milestones.length > 0 && (        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h2 className="font-semibold text-gray-700 mb-3">
            💰 Payment Milestones
          </h2>
          <div className="space-y-2">
            {milestones.map(m => (
              <div key={m.id}
                className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    m.status === "completed" ? "bg-green-500" : "bg-gray-300"
                  }`} />
                  <span className={m.status === "completed" ? "line-through text-gray-400" : "text-gray-700"}>
                    {m.item_name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {m.milestone_percent > 0 && (
                    <span className="text-xs text-gray-400">{m.milestone_percent}%</span>
                  )}
                  {m.status === "completed" ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      Done
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate(`/projects/${id}/checklist`)}
                      className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold hover:bg-amber-200">
                      Pending
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ACTIONS */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-3">
        <h2 className="font-semibold text-gray-700">Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setTab("checklist")}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
            📋 View Checklist
          </button>
          {/* Technician has no materials/documents routes in App.js —
              these buttons used to bounce them Home. */}
          {!isTechnician && (
            <>
              <button
                onClick={() => setTab("materials")}
                className="px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700">
                📦 Material Control
              </button>
              <button
                onClick={() => setTab("documents")}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700">
                📁 Documents
              </button>
            </>
          )}
          {/* /billing/create is routed for owner (and accounts) only in
              App.js — showing this to sm/manager just bounced them Home. */}
          {role === "owner" && (
            <button
              onClick={() => navigate(`/billing/create`, {
                state: {
                  from_project:      true,
                  project_id:        id,
                  project_name:      project.project_name,
                  client_id:         project.client_id,
                  client_name:       project.client_name,
                  site_id:           project.site_id,
                  site_name:         project.site_name,
                  chargeable_amount: project.total_value || 0,
                  service_complaint: project.project_name,
                }
              })}
              className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700">
              🧾 Generate Invoice
            </button>
          )}
          {/* /services is not routed for site_engineer in App.js */}
          {!isSiteEngineer && (
          <button
            onClick={() => navigate(`/services`, {
              state: {
                from_project: true,
                project_id:   id,
                project_name: project.project_name,
                client_id:    project.client_id,
                client_name:  project.client_name,
                site_id:      project.site_id,
                site_name:    project.site_name,
              }
            })}
            className="px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700">
            🔧 Create Service Job
          </button>
          )}
        </div>

        {/* Status Change — Manager/Owner only */}
        {isManager && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2">Change Status:</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.filter(s => s !== project.status).map(s => (
                <button key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={saving}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition disabled:opacity-50 ${
                    STATUS_COLORS[s] || "bg-gray-100 text-gray-600"
                  }`}>
                  → {s.replace("_", " ").toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

    {/* LINKED SERVICES */}
      {linkedServices.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h2 className="font-semibold text-gray-700 mb-3">
            🔧 Linked Service Jobs ({linkedServices.length})
          </h2>
          <div className="space-y-2">
            {linkedServices.map(s => (
              <div key={s.id}
                onClick={() => navigate(`/services/${s.id}`)}
                className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:bg-gray-50 cursor-pointer transition text-sm">
                <div>
                  <p className="font-medium text-gray-800">
                    {s.job_category === "amc_visit"    ? "AMC Visit"     :
                     s.job_category === "preventive"   ? "PM"            :
                     s.job_category === "installation" ? "Installation"  :
                     s.complaint || "Service"}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {s.scheduled_date || "—"}
                    {s.assigned_techs?.[0]?.name && ` · ${s.assigned_techs[0].name}`}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  s.status === "closed"     ? "bg-blue-100 text-blue-700"  :
                  s.status === "in_progress"? "bg-green-100 text-green-700":
                  s.status === "assigned"   ? "bg-purple-100 text-purple-700":
                  "bg-gray-100 text-gray-600"
                }`}>
                  {s.status?.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      </>)}

      {/* EDIT MODAL */}
      {editOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-bold text-gray-800 text-lg">Edit Project</h3>
              <button onClick={() => setEditOpen(false)}
                className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Project Name *</label>
                <input value={editForm.project_name}
                  onChange={e => setEditForm(f => ({ ...f, project_name: e.target.value }))}
                  className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Client</label>
                  <select value={editForm.client_id}
                    onChange={e => {
                      const c = clients.find(c => c.id === e.target.value);
                      if (!c) return;
                      setEditForm(f => ({ ...f, client_id: c.id, client_name: c.client_name, site_id: "", site_name: "" }));
                      fetchSites(c.id);
                    }}
                    className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
                    <option value="">Select Client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Site</label>
                  <div className="flex gap-2 items-center">
                    <select value={editForm.site_id}
                      onChange={e => {
                        const s = sites.find(s => s.id === e.target.value);
                        if (!s) return;
                        setEditForm(f => ({ ...f, site_id: s.id, site_name: s.site_name || s.site_title }));
                      }}
                      disabled={!editForm.client_id}
                      className="border p-2 rounded-xl flex-1 text-sm outline-none bg-white disabled:opacity-50">
                      <option value="">Select Site</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.site_name || s.site_title}</option>)}
                    </select>
                    {/* ✅ Quick create site button */}
                    <button
                      type="button"
                      onClick={() => setShowSiteModal(true)}
                      disabled={!editForm.client_id}
                      title={editForm.client_id ? "Add new site" : "Select client first"}
                      className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border transition
                        ${editForm.client_id
                          ? "border-indigo-300 text-indigo-500 bg-indigo-50 hover:bg-indigo-100 cursor-pointer"
                          : "border-slate-200 text-slate-300 cursor-not-allowed bg-white"
                        }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>

                  {/* ✅ Quick Create Site Modal */}
                  <QuickCreateModal
                    open={showSiteModal}
                    onClose={() => setShowSiteModal(false)}
                    title="Add New Site"
                  >
                    <QuickCreateSite
                      companyId={company_id}
                      clientId={editForm.client_id}
                      clientName={editForm.client_name}
                      onCreated={async (newSite) => {
                        // ✅ sites list refresh + auto-select
                        await fetchSites(editForm.client_id);
                        setEditForm(f => ({
                          ...f,
                          site_id:   newSite.id,
                          site_name: newSite.site_name,
                        }));
                        setShowSiteModal(false);
                      }}
                      onCancel={() => setShowSiteModal(false)}
                    />
                  </QuickCreateModal>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Project Type</label>
                  <select value={editForm.project_type}
                    onChange={e => setEditForm(f => ({ ...f, project_type: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
                    <option value="installation">Installation</option>
                    <option value="amc">AMC</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="supply">Supply</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Contract Value ₹</label>
                  <input type="number" value={editForm.total_value}
                    onChange={e => setEditForm(f => ({ ...f, total_value: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-green-300" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Start Date</label>
                  <input type="date" value={editForm.start_date}
                    onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">End Date</label>
                  <input type="date" value={editForm.end_date}
                    onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none" />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Description</label>
                <textarea rows={2} value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  className="border p-2 rounded-xl w-full text-sm outline-none resize-none" />
              </div>

              {/* Site Engineer Assignment — controls who can see/log work
                  against this project via the scoped site_engineer role */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-2">
                  Assign Site Engineers
                </label>
                {siteEngineers.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    No Site Engineer users found — add one via User Management first.
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-32 overflow-y-auto border rounded-xl p-2">
                    {siteEngineers.map(se => {
                      const checked = (editForm.site_engineer_ids || []).includes(se.id);
                      return (
                        <label key={se.id} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={checked}
                            onChange={e => {
                              const current = editForm.site_engineer_ids || [];
                              const updated = e.target.checked
                                ? [...current, se.id]
                                : current.filter(uid => uid !== se.id);
                              setEditForm(f => ({ ...f, site_engineer_ids: updated }));
                            }} />
                          {se.name || se.email || se.id}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Contacts */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-semibold text-gray-500">Site Contacts</label>
                  <button onClick={() => setEditForm(f => ({
                    ...f, contacts: [...(f.contacts || []), { name: "", role: "", phone: "" }]
                  }))} className="text-xs text-green-600 font-semibold">+ Add</button>
                </div>
                {(editForm.contacts || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                    <input placeholder="Name" value={c.name}
                      onChange={e => {
                        const updated = [...editForm.contacts];
                        updated[i].name = e.target.value;
                        setEditForm(f => ({ ...f, contacts: updated }));
                      }}
                      className="border p-2 rounded-lg text-xs outline-none" />
                    <input placeholder="Role" value={c.role}
                      onChange={e => {
                        const updated = [...editForm.contacts];
                        updated[i].role = e.target.value;
                        setEditForm(f => ({ ...f, contacts: updated }));
                      }}
                      className="border p-2 rounded-lg text-xs outline-none" />
                    <div className="flex gap-1">
                      <input placeholder="Phone" value={c.phone}
                        onChange={e => {
                          const updated = [...editForm.contacts];
                          updated[i].phone = e.target.value;
                          setEditForm(f => ({ ...f, contacts: updated }));
                        }}
                        className="border p-2 rounded-lg text-xs outline-none flex-1" />
                      {(editForm.contacts || []).length > 1 && (
                        <button onClick={() => setEditForm(f => ({
                          ...f, contacts: f.contacts.filter((_, idx) => idx !== i)
                        }))} className="text-red-400 text-sm">✕</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditOpen(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={saving}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-50">
                {saving ? "Saving..." : "Save Changes ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
